//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import FacebookPageId from '@models/FacebookPageId';
import { getHashtagWithoutHashSymbol } from '@utils/social-text-utils/socialTextUtils';
import {
  ISocialTextEntity,
  SocialTextEntityType,
  SocialTextParseMethod,
} from '@utils/social-text-utils/socialTextUtilsModel';
import { parseTwitterText } from '@utils/social-text-utils/twitterTextUtils';

// This will match a page tag of the form @[page_id] that is prepended by a space
// or the beginning of the line and appended by a space, new line or
// the end of the line
const FACEBOOK_PAGE_TAG = '(\\s|^)(@\\[\\d+\\])';

const FACEBOOK_PAGE_TAG_REGEX = new RegExp(`${FACEBOOK_PAGE_TAG}`);

/**
 * Takes text as input and parses it to return an array of typed Facebook text entities.
 * For example, the input
 * 'Hello @curalate! #shippy test https://abc.com and a page tag @[123456789]'
 * would result in output:
 *
 * [
 *   { type: 'text', value: 'Hello '},
 *   { type: 'mention', value: '@curalate'},
 *   { type: 'text', value: '! '},
 *   { type: 'hashtag', value: '#shippy'},
 *   { type: 'text', value: ' test '},
 *   { type: 'url', value: 'https://abc.com'},
 *   { type: 'Text', value: ' and a page tag '},
 *   { type: 'FacebookMention', value: '@[123456789]'},
 * ]
 *
 * We are reusing the Twitter parsing function so that we can identify urls, hashtags, etc.
 * This function uses a Twitter library for parsing these that is heavily tested.
 *
 * @returns ISocialTextParsedEntity[] The parsed array of objects with properties
 * { type, value }, where type is one of SocialTextParsedEntityType.
 */
export const parseFacebookText: SocialTextParseMethod = (text) => {
  const extractedEntities = parseTwitterText(text);
  const parsedEntities: ISocialTextEntity[] = [];

  extractedEntities.forEach((entity) => {
    if (entity.type === SocialTextEntityType.Text) {
      const facebookEntities = extractAndCategorizeFacebookMentions(
        entity.value,
        entity.start,
      );
      facebookEntities.forEach((entity) => parsedEntities.push(entity));
    } else {
      parsedEntities.push(entity);
    }
  });

  return parsedEntities;
};

const extractAndCategorizeFacebookMentions = (
  text: string,
  sectionStartIndex: number = 0,
): ISocialTextEntity[] => {
  const extractedFacebookEntities = extractFacebookEntities(text);
  const parsedTextValues: ISocialTextEntity[] = [];

  let startIndex = sectionStartIndex;
  let endIndex = sectionStartIndex;

  extractedFacebookEntities.forEach((entity) => {
    if (entity) {
      startIndex = endIndex;
      endIndex = startIndex + entity.length;

      if (FACEBOOK_PAGE_TAG_REGEX.test(entity)) {
        parsedTextValues.push({
          type: SocialTextEntityType.FacebookPageMention,
          value: entity,
          start: startIndex,
          end: endIndex,
        });
      } else {
        parsedTextValues.push({
          type: SocialTextEntityType.Text,
          value: entity,
          start: startIndex,
          end: endIndex,
        });
      }
    }
  });

  return parsedTextValues;
};

const extractFacebookEntities = (text: string): string[] => {
  // Initialize variables for loop
  const entities = [];
  let partialText = text;

  while (partialText.length > 0) {
    // Find Facebook page tags (@[page_id])
    const firstMention = FACEBOOK_PAGE_TAG_REGEX.exec(partialText);
    if (firstMention) {
      // If the regex match begins with a space, update the index
      if (partialText[firstMention.index] === ' ') {
        firstMention.index += 1;
      }
      if (firstMention.index > 0) {
        const textBefore = partialText.slice(0, firstMention.index);
        entities.push(textBefore);
        partialText = partialText.slice(firstMention.index);
      } else {
        const mentionMatch = firstMention[0];
        if (mentionMatch) {
          entities.push(mentionMatch);
          partialText = partialText.slice(mentionMatch.length);
        }
      }
    } else {
      // If there are no mentions push the rest of the text as an entity
      // and set partialText to an empty string to finish the loop.
      entities.push(partialText);
      partialText = '';
    }
  }

  return entities;
};

/**
 * Takes a page tag with @[page_id] syntax and returns only the page id
 * @example extractFacebookPageIdFromTag('@[123456789]') = 123456789
 *
 * @returns FacebookPageId The FacebookPageId within the @[page_id] syntax
 */
export function extractFacebookPageIdFromTag(pageTag: string): FacebookPageId {
  return pageTag.length > 3
    ? FacebookPageId(pageTag.slice(2, -1))
    : FacebookPageId('');
}

/**
 * Takes a page tag and produces the Facebook link corresponding to the page id
 * @example createFacebookPageMentionLink('@[123456789]') = https://facebook.com/123456789
 *
 * @returns string The Facebook page id link
 */
export function createFacebookPageMentionLink(pageTag: string): string {
  return `https://facebook.com/${extractFacebookPageIdFromTag(pageTag)}`;
}

/**
 * Takes a hashtag and produces the Facebook link corresponding to the hashtag
 * @example createFacebookPageMentionLink('#summer') = https://facebook.com/hashtag/summer
 *
 * @returns string The Facebook hashtag link
 */
export function createFacebookHashtagLink(hashtag: string): string {
  return `https://facebook.com/hashtag/${getHashtagWithoutHashSymbol(hashtag)}`;
}
